import { CheckboxSelectRow } from "@/features/credentials/components/checkbox-select-row";
import { addSelectedIds, removeSelectedIds } from "@/features/credentials/lib/toggle-selected-ids";
import { Badge, Checkbox, Input, ScrollArea } from "@cmde/shadcn";
import { Loader2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface CheckboxSelectItem {
  id: string;
  name: string;
}

export function CheckboxSelectList({
  items,
  value,
  onChange,
  isLoading,
  searchPlaceholder,
  emptyLabel,
  loadingLabel,
  selectAllLabel,
  unnamedLabel,
  otherSelectedLabel,
}: {
  items: CheckboxSelectItem[];
  value: string[];
  onChange: (nextIds: string[]) => void;
  isLoading?: boolean;
  searchPlaceholder: string;
  emptyLabel: string;
  loadingLabel: string;
  selectAllLabel: string;
  unnamedLabel: string;
  otherSelectedLabel: string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const selectedIdSet = new Set(value);
  const itemIds = items.map((item) => item.id);
  const namesById = useMemo(() => new Map(items.map((item) => [item.id, item.name])), [items]);
  const selectedCount = items.filter((item) => selectedIdSet.has(item.id)).length;
  const allSelected = itemIds.length > 0 && selectedCount === itemIds.length;

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, search]);

  const orphanItems = useMemo(
    () =>
      value
        .filter((id) => !namesById.has(id))
        .filter((id) => {
          const query = search.trim().toLowerCase();
          const name = unnamedLabel;
          return !query || name.toLowerCase().includes(query) || id.toLowerCase().includes(query);
        })
        .map((id) => ({ id, name: unnamedLabel })),
    [namesById, search, unnamedLabel, value],
  );

  const itemName = (item: CheckboxSelectItem) => item.name || unnamedLabel;

  if (isLoading && items.length === 0) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2Icon className="size-4 animate-spin" />
        {loadingLabel}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border bg-white dark:bg-transparent">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={searchPlaceholder}
          className="h-8 max-w-xs"
        />
      </div>

      <div className="flex items-center gap-2 border-b py-2 pr-4 pl-4 hover:bg-muted/40">
        <Checkbox
          variant="simple"
          id="checkbox-select-all"
          checked={allSelected}
          disabled={itemIds.length === 0 || isLoading}
          aria-label={selectAllLabel}
          onCheckedChange={(checked) =>
            onChange(checked ? addSelectedIds(value, itemIds) : removeSelectedIds(value, itemIds))
          }
        />
        <label htmlFor="checkbox-select-all" className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
          <span className="truncate">{selectAllLabel}</span>
          {selectedCount > 0 ? (
            <Badge variant="secondary" className="text-xs">
              {t("credentials.create.fields.selected_count", { count: selectedCount })}
            </Badge>
          ) : null}
        </label>
      </div>

      {filteredItems.length === 0 && orphanItems.length === 0 ? (
        <p className="text-muted-foreground p-4 text-sm">{emptyLabel}</p>
      ) : (
        <ScrollArea className="h-80">
          <div className="divide-y divide-border/50">
            {filteredItems.map((item) => (
              <CheckboxSelectRow
                key={item.id}
                checkboxId={`checkbox-select-${item.id}`}
                name={itemName(item)}
                checked={selectedIdSet.has(item.id)}
                className="pl-4"
                onCheckedChange={(checked) =>
                  onChange(checked ? addSelectedIds(value, [item.id]) : removeSelectedIds(value, [item.id]))
                }
              />
            ))}
            {orphanItems.length > 0 ? (
              <div className="odd:bg-muted/10 dark:odd:bg-white/3">
                <div className="flex items-center gap-2 py-2 pr-4 pl-4">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{otherSelectedLabel}</span>
                  <Badge variant="secondary" className="text-xs">
                    {t("credentials.create.fields.selected_count", { count: orphanItems.length })}
                  </Badge>
                </div>
                {orphanItems.map((item) => (
                  <CheckboxSelectRow
                    key={item.id}
                    checkboxId={`checkbox-select-other-${item.id}`}
                    name={item.name}
                    checked={selectedIdSet.has(item.id)}
                    className="pl-4"
                    onCheckedChange={(checked) =>
                      onChange(checked ? addSelectedIds(value, [item.id]) : removeSelectedIds(value, [item.id]))
                    }
                  />
                ))}
              </div>
            ) : null}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
